const DEFAULT_PARAMS = {
  salesHistoryWeeks: '',
  projectedSales: '',
  projectedGc: '',
  week1Gc: '',
  week2Gc: '',
  week3Gc: '',
  week1Sales: '',
  week2Sales: '',
  week3Sales: '',
  week1Label: '',
  week2Label: '',
  week3Label: '',
  includeClosingStock: true,
  includeIssuedStock: true,
  includeProductionStock: false,
  includeDamagedStock: false,
  includeReturnsStock: false,
};

export function defaultPlanningValue(field) {
  if (field === 'leadTime') return '1';
  if (field === 'bufferPercent') return '10';
  if (field === 'projectedGrowth') return '20';
  return '';
}

function asPlanningString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function productSpsValue(row, index) {
  if (!row || typeof row !== 'object') return '';
  const n = String(index);
  const value = row[`sps_${n}`] ?? row[`sps${n}`] ?? row[`SPS_${n}`] ?? row[`SPS${n}`];
  return value == null || value === '' ? '' : String(value);
}

export function planningFieldFromApi(row, field) {
  if (!row || typeof row !== 'object') return '';
  if (field === 'leadTime') return asPlanningString(row.lead_time ?? row.leadTime);
  if (field === 'bufferPercent') {
    return asPlanningString(row.buffer ?? row.buffer_percent ?? row.bufferPercent);
  }
  if (field === 'projectedGrowth') {
    return asPlanningString(
      row.guest_count ?? row.guestCount ?? row.projected_growth ?? row.projectedGrowth
    );
  }
  return '';
}

export function planningValuesToApi(values = {}) {
  const toNumberOrSkip = (value) => {
    if (value == null || String(value).trim() === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    lead_time: toNumberOrSkip(values.leadTime),
    buffer: toNumberOrSkip(values.bufferPercent),
    guest_count: toNumberOrSkip(values.projectedGrowth),
  };
}

export function withCategoryPlanning(currentMap, categoryIds, categories = []) {
  const byId = Object.fromEntries(
    (categories || []).map((cat) => [String(cat.id), cat])
  );
  const next = { ...(currentMap || {}) };
  (categoryIds || []).forEach((id) => {
    const rec = { ...(next[id] || {}) };
    const fromApi = byId[String(id)] || {};
    // leadTime is defined at subcategory level, not category.
    ['bufferPercent', 'projectedGrowth'].forEach((key) => {
      if (rec[key] == null || String(rec[key]).trim() === '') {
        rec[key] = fromApi[key] || defaultPlanningValue(key);
      }
    });
    next[id] = rec;
  });
  return next;
}

export function withSubcategoryPlanning(currentMap, subcategories = []) {
  const next = { ...(currentMap || {}) };
  (subcategories || []).forEach((sub) => {
    if (!sub?.id) return;
    const rec = { ...(next[sub.id] || {}) };
    if (rec.leadTime == null || String(rec.leadTime).trim() === '') {
      rec.leadTime = sub.leadTime || defaultPlanningValue('leadTime');
    }
    next[sub.id] = rec;
  });
  return next;
}

export function categoryPlanningValue(tool, sheet, field) {
  const rec = (tool?.planningByCategory || {})[sheet] || {};
  if (rec[field] != null && String(rec[field]).trim() !== '') return String(rec[field]);
  // if (field === 'leadTime') {
  //   const old = (tool?.leadTimeByCategory || {})[sheet];
  //   if (old != null && String(old).trim() !== '') return String(old);
  // }
  return defaultPlanningValue(field);
}

export function itemSubcategoryKey(item) {
  if (!item) return '';
  if (item.subcategoryKey) return String(item.subcategoryKey);
  return subCategoryKey(item.sheet, item.subcategoryId);
}

export function subcategoryPlanningValue(tool, subKey, field) {
  const rec = (tool?.planningBySubcategory || {})[subKey] || {};
  if (rec[field] != null && String(rec[field]).trim() !== '') return String(rec[field]);
  return field === 'leadTime' ? defaultPlanningValue('leadTime') : '';
}

export function itemPlanningValue(item, tool, field) {
  if (!item) return '';
  if (field === 'leadTime') {
    const subKey = itemSubcategoryKey(item);
    const subRec = (tool?.planningBySubcategory || {})[subKey] || {};
    if (subRec.leadTime != null && String(subRec.leadTime).trim() !== '') {
      return String(subRec.leadTime);
    }
    if (item.subcategoryLeadTime != null && String(item.subcategoryLeadTime).trim() !== '') {
      return String(item.subcategoryLeadTime);
    }
    return defaultPlanningValue('leadTime');
  }
  const key = item.selectionKey || `${item.sheet}::${item.itemCode}`;
  const skuRec = (tool?.planningBySku || {})[key] || {};
  if (skuRec[field] != null && String(skuRec[field]).trim() !== '') return String(skuRec[field]);
  const catRec = (tool?.planningByCategory || {})[item.sheet] || {};
  if (catRec.userEdited && catRec[field] != null && String(catRec[field]).trim() !== '') {
    return String(catRec[field]);
  }
  if (item[field] != null && String(item[field]).trim() !== '') {
    const n = Number(item[field]);
    const isZero = Number.isFinite(n) && n === 0;
    if (!isZero || field === 'projectedGrowth') return String(item[field]);
  }
  return categoryPlanningValue(tool, item.sheet, field);
}

export function averageSelectedSkuPlanning(tool, field) {
  const items = catalogItemsForTool(tool);
  if (!items.length) return defaultPlanningValue(field);
  const sum = items.reduce(
    (total, item) => total + Number(itemPlanningValue(item, tool, field) || 0),
    0
  );
  return String(sum / items.length);
}

export function selectedSkuPlanningComplete(tool) {
  const items = catalogItemsForTool(tool);
  if (!items.length) return false;
  return items.every((item) =>
    ['leadTime', 'bufferPercent', 'projectedGrowth'].every(
      (field) => String(itemPlanningValue(item, tool, field) ?? '').trim() !== ''
    )
  );
}

function subCategoryKey(sheet, subName) {
  return `${sheet}::${subName || ''}`;
}

/** Marker: category selected but no subcategories checked. */
export const NONE_SUBCATEGORY = '__none__';

export function noneSubKey(categoryId) {
  return `${categoryId}::${NONE_SUBCATEGORY}`;
}

export function isNoneSubKey(key) {
  return String(key || '').endsWith(`::${NONE_SUBCATEGORY}`);
}

export function listFromApiResponse(res) {
  const list = res?.data ?? res ?? [];
  if (Array.isArray(list)) return list;
  if (Array.isArray(list?.data)) return list.data;
  return [];
}

export function mapApiCategory(row) {
  return {
    id: String(row?.id ?? ''),
    name: row?.name || String(row?.id ?? ''),
    items: row?.products_count ?? row?.items_count ?? row?.items ?? '',
    // leadTime: planningFieldFromApi(row, 'leadTime'),
    bufferPercent: planningFieldFromApi(row, 'bufferPercent'),
    projectedGrowth: planningFieldFromApi(row, 'projectedGrowth'),
  };
}

export function mapApiSubcategory(row, categoryNameById = {}) {
  const categoryId = String(
    row?.product_category_id ?? row?.product_category?.id ?? row?.productCategoryId ?? ''
  );
  const subId = String(row?.id ?? '');
  return {
    id: subCategoryKey(categoryId, subId),
    name: row?.name || subId,
    categoryId,
    categoryName:
      categoryNameById[categoryId] ||
      row?.product_category_name ||
      row?.product_category?.name ||
      '',
    items: row?.products_count ?? row?.items_count ?? row?.items ?? '',
    leadTime: planningFieldFromApi(row, 'leadTime'),
  };
}

export function mapApiProductToCatalogItem(row, categoryNameById = {}, subcategoryNameById = {}) {
  const categoryId = String(
    row?.product_category_id ??
      row?.productCategoryId ??
      row?.category_id ??
      row?.categoryId ??
      ''
  );
  const subcategoryId = String(
    row?.product_subcategory_id ??
      row?.productSubcategoryId ??
      row?.subcategory_id ??
      row?.subcategoryId ??
      row?.product_subcategory?.id ??
      ''
  );
  const itemCode = String(row?.item_code || row?.itemCode || row?.code || row?.id || '').trim();
  const itemName = row?.item_name || row?.name || row?.itemName || row?.product_name || '';
  const nestedSub = row?.product_subcategory || row?.productSubcategory || row?.subcategory;
  return {
    id: String(row?.id ?? itemCode),
    productId: String(row?.id ?? row?.product_id ?? ''),
    sheet: categoryId,
    category:
      categoryNameById[categoryId] ||
      row?.product_category_name ||
      row?.productCategoryName ||
      row?.product_category?.name ||
      row?.category_name ||
      row?.categoryName ||
      '',
    subCategory:
      subcategoryNameById[subcategoryId] ||
      subcategoryNameById[`${categoryId}::${subcategoryId}`] ||
      row?.product_subcategory_name ||
      row?.productSubcategoryName ||
      row?.subcategory_name ||
      row?.subcategoryName ||
      (nestedSub && typeof nestedSub === 'object' ? nestedSub.name : '') ||
      '',
    subcategoryId,
    itemCode,
    itemName,
    sps1: productSpsValue(row, 1),
    sps2: productSpsValue(row, 2),
    sps3: productSpsValue(row, 3),
    selectionKey: `${categoryId}::${itemCode || row?.id}`,
    // leadTime: planningFieldFromApi(row, 'leadTime'),
    bufferPercent: planningFieldFromApi(row, 'bufferPercent'),
    projectedGrowth: planningFieldFromApi(row, 'projectedGrowth'),
  };
}

export function filterCatalogProducts(items, categoryIds, subKeys) {
  const sheets = (categoryIds || []).map(String).filter(Boolean);
  if (!sheets.length) return [];
  return (items || []).filter((item) => {
    if (!sheets.includes(String(item.sheet))) return false;
    const keysForSheet = (subKeys || []).filter((key) => key.startsWith(`${item.sheet}::`));
    if (keysForSheet.some((key) => isNoneSubKey(key))) return false;
    if (!keysForSheet.length) return true;
    return keysForSheet.includes(subCategoryKey(item.sheet, item.subcategoryId));
  });
}

export function categoryDisplayName(tool, id) {
  return tool?.categoryLabels?.[id] || id;
}

export function subcategoryDisplayName(tool, key) {
  if (tool?.subCategoryLabels?.[key]) return tool.subCategoryLabels[key];
  const [sheet, name] = String(key || '').split('::');
  const cat = categoryDisplayName(tool, sheet);
  return name ? `${cat} · ${name}` : cat;
}

/** Resolve SKU subcategory label from catalog row + wizard labels. */
export function resolveItemSubCategory(item, tool) {
  const direct = String(item?.subCategory || '').trim();
  if (direct) return direct;
  const subcategoryId = String(item?.subcategoryId || '').trim();
  if (!subcategoryId) return '';
  const compositeKey = `${item?.sheet || ''}::${subcategoryId}`;
  const labeled = tool?.subCategoryLabels?.[compositeKey];
  if (labeled) {
    const text = String(labeled).trim();
    const parts = text.split(' · ');
    return parts.length > 1 ? parts.slice(1).join(' · ').trim() : text;
  }
  return '';
}

export function catalogItemsForTool(tool, options = {}) {
  if (!tool) return [];
  const selectedKeys = Array.isArray(tool.selectedItemKeys) ? tool.selectedItemKeys : null;
  const source = Array.isArray(tool.productCatalog) ? tool.productCatalog : [];
  return source.filter((item) => {
    if (options.ignoreSelectedItems || selectedKeys == null) return true;
    return selectedKeys.includes(item.selectionKey);
  });
}

function emptySelectionDefaults() {
  return {
    categories: [],
    subCategories: [],
    selectedItemKeys: null,
    planningByCategory: {},
    planningBySubcategory: {},
    planningBySku: {},
    categoryLabels: {},
    subCategoryLabels: {},
    productCatalog: [],
    dailySalesRows: [],
    categoriesDefaulted: false,
    closingStockDraft: null,
  };
}

export function wizardDefaultsForToolType(productType) {
  const name = String(productType?.name || '').trim() || 'Items';
  const idNum = Number(productType?.id);
  const id = Number.isFinite(idNum) ? String(idNum) : String(productType?.id ?? '').trim();
  return {
    toolType: id,
    productTypeId: id,
    productTypeName: name,
    ...emptySelectionDefaults(),
    toolName: '',
    toolCode: '',
    toolDescription: '',
    outletId: '',
    outletName: '',
    parameters: { ...DEFAULT_PARAMS },
  };
}
